import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Typography, Grid, Box, MenuItem } from "@mui/material";
import { ValidatorForm } from "react-material-ui-form-validator";
import ValidationRules from "../../../common/ValidationRules";
import { withTranslation } from "react-i18next";
import { logMsg } from "../../../common/Logger";
import {
  displayError,
  ERROR_PROCESSING,
  is404,
  isProcessingError,
  PAGE_ERROR,
} from "../../../errors/ErrorConstants";
import SortData from "../../../utils/SortUtil";
import ConfirmDialog from "../../../shared/confirmDialog/ConfirmDialog";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import CategorySettingsClient from "../../../clients/CategorySettingsClient";
import CategorySettingsRowHeader from "./CategorySettingsRowHeader";
import CategoryDropDown from "./CategoryDropDown";
import PublishButton from "../PublishButton";
import fordStyles from "../namePlateSettings/NamePlateSettings-Ford.module.scss";
import lincolnStyles from "../namePlateSettings/NamePlateSettings-Lincoln.module.scss";
import { BRAND_NAMES } from "../../../../src/common/Constants.js";
import * as AnalyticsService from "../../../shared/services/analytics/AnalyticsService";
import CapitalizeEachWord from "../../../utils/CapitalizeEachWord";
import CapitalizeFirstWord from "../../../utils/CapitalizeFirstWord";

const SELECT = "select";
const CATEGORY = "CATEGORY";

const CategorySettings = (props) => {
  const { user, hideOrShow, t, confirmDialogProps } = props;

  const isFordBrand = IS_FORD_BRAND(user.brand);
  const styles = isFordBrand ? fordStyles : lincolnStyles;

  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(SELECT);
  const [categoryDropDownValues, setCategoryDropDownValues] = useState([]);
  const [categorySettings, setCategorySettings] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [markupEditEnabled, setMarkupEditEnabled] = useState(false);
  const [laborRateEditEnabled, setLaborRateEditEnabled] = useState(false);
  const [enablePublishBtn, setEnablePublishBtn] = useState(false);
  const [overAllMarkup, setOverAllMarkup] = useState("");
  const [overAllLaborRate, setOverAllLaborRate] = useState("");
  const [hasError, setHasError] = useState([]);
  const [overridesApplied, setOverridesApplied] = useState(false);
  const [categorySettingsByGroupName, setCategorySettingsByGroupName] =
    useState([]);
  const [showCategoryChangeDialog, setShowCategoryChangeDialog] =
    useState(false);

  const targetCategoryRef = useRef(null);

  const confirmDialogText = {
    title: t("CategorySettings.dialogConfirmTitle"),
    bodyText: t("CategorySettings.dialogConfirmBody"),
    confirm: isFordBrand
      ? t("CategorySettings.dialogFordPublish")
      : t("CategorySettings.dialogLincolnConfirm"),
    cancel: isFordBrand
      ? t("CategorySettings.dialogFordGoBack")
      : t("CategorySettings.dialogLincolnCancel"),
  };

  const setOriginalData = (data) => {
    setCategorySettingsByGroupName(data);
    setIsDataLoaded(true);
  };

  const filterCategorySettingsByCatalogId = (data) => {
    return data.map((group) => {
      const seenCatalogIds = new Set();
      return group.categorySettingsSubGroup.filter((item) => {
        if (seenCatalogIds.has(item.catalogId)) return false;
        seenCatalogIds.add(item.catalogId);
        return true;
      });
    });
  };

  const getCategorySettings = () => {
    hideOrShow(true);
    CategorySettingsClient.getDealerCategorySettings(
      user,
      IS_FORD_BRAND(user.brand) ? BRAND_NAMES.FORD : BRAND_NAMES.LINCOLN
    )
      .then((data) => {
        const dropDownValues = data.map((c) => c.groupName);
        setCategorySettings(data);
        setCategoryDropDownValues(dropDownValues);
        setIsDataLoaded(true);
      })
      .catch((error) => {
        logMsg("reason", error);
        if (is404(error)) {
          setHasError([PAGE_ERROR]);
        } else if (isProcessingError(error)) {
          setHasError([ERROR_PROCESSING]);
        }
      })
      .finally(() => {
        hideOrShow(false);
      });
  };

  // Intentionally mount-only: page-load analytics, validation rule
  // registration, and initial data fetch should happen exactly once.
  // Re-firing on `user` changes would double-fetch on auth refresh.
  useEffect(() => {
    AnalyticsService.trackPageLoadEvent(
      "fv:emp:accessories pricing:category",
      user
    );
    ValidationRules();
    getCategorySettings();
  }, []);

  useEffect(() => {
    const shouldEnable =
      (laborRateEditEnabled && overAllLaborRate !== "") ||
      (markupEditEnabled && overAllMarkup !== "");
    setEnablePublishBtn(shouldEnable);
  }, [
    laborRateEditEnabled,
    overAllLaborRate,
    markupEditEnabled,
    overAllMarkup,
  ]);

  useEffect(() => {
    if (user && typeof user.showWarning === "function") {
      user.showWarning(enablePublishBtn);
    }
  }, [enablePublishBtn, user]);

  const handleConfirm = (e) => {
    if (e) {
      e.preventDefault();
    }
    setConfirmDialog(true);
  };

  const handleSubmit = () => {
    hideOrShow(true);
    const markupValue = Number.parseFloat(overAllMarkup);
    const laborRateValue = Number.parseFloat(overAllLaborRate);
    const applyMarkup = markupEditEnabled && overAllMarkup !== "";
    const applyLaborRate = laborRateEditEnabled && overAllLaborRate !== "";

    // Clone deeply enough that we never mutate the source `categorySettings`
    // state (or the API response objects it came from).
    const updatedCategorySettings = categorySettings
      .filter((group) => group.groupName === selectedCategory)
      .map((group) => ({
        ...group,
        categorySettingsSubGroup: group.categorySettingsSubGroup.map(
          (subGroup) => {
            const next = { ...subGroup };
            if (markupValue) next.markup = markupValue;
            if (laborRateValue) next.laborRate = laborRateValue;
            if (applyMarkup) next.markupType = CATEGORY;
            if (applyLaborRate) next.laborRateType = CATEGORY;
            return next;
          }
        ),
      }));

    CategorySettingsClient.saveDealerCategorySettings(
      user,
      updatedCategorySettings,
      applyLaborRate,
      applyMarkup
    )
      .then(() => {
        const byCatId = filterCategorySettingsByCatalogId(
          updatedCategorySettings
        );
        setOriginalData([
          {
            groupName: selectedCategory,
            categorySettingsSubGroup: byCatId[0],
          },
        ]);
        setMarkupEditEnabled(false);
        setLaborRateEditEnabled(false);
        setConfirmDialog(false);
        setOverridesApplied(true);
      })
      .catch((error) => {
        logMsg("reason", error);
        if (is404(error)) {
          setHasError([PAGE_ERROR]);
        } else if (isProcessingError(error)) {
          setHasError([ERROR_PROCESSING]);
        }
      })
      .finally(() => {
        hideOrShow(false);
        if (user && typeof user.showWarning === "function") {
          user.showWarning(false);
        }
      });
  };

  const handleClose = () => {
    setConfirmDialog(false);
    setShowCategoryChangeDialog(false);
  };

  const updateMarkupEditEnabled = () => {
    setOverAllMarkup("");
    setMarkupEditEnabled((prev) => !prev);
    setOverridesApplied(false);
  };

  const updateLaborRateEditEnabled = () => {
    setOverAllLaborRate("");
    setLaborRateEditEnabled((prev) => !prev);
    setOverridesApplied(false);
  };

  const updateModel = (event) => {
    if (event.target.name === "markup") {
      setOverAllMarkup(event.target.value);
    } else {
      setOverAllLaborRate(event.target.value);
    }
  };

  const filterCategorySettings = (newGroup) => {
    const byCatId = filterCategorySettingsByCatalogId(
      categorySettings.filter((dealer) => dealer.groupName === newGroup)
    );
    setOriginalData([
      {
        groupName: newGroup,
        categorySettingsSubGroup: byCatId[0],
      },
    ]);
  };

  const changeCategory = () => {
    const newCat = targetCategoryRef.current;
    setSelectedCategory(newCat);
    setShowCategoryChangeDialog(false);
    setMarkupEditEnabled(false);
    setOverridesApplied(false);
    setOverAllMarkup("");
    filterCategorySettings(newCat);
  };

  const updateCategory = (event) => {
    if (event) {
      event.preventDefault();
    }
    targetCategoryRef.current = event.target.value;
    if (enablePublishBtn) {
      setShowCategoryChangeDialog(true);
    } else {
      changeCategory();
    }
  };

  const sortColumns = (sortByCol) => {
    let sortDir = "ASC";
    if (sortByCol === sortKey) {
      sortDir = sortDirection === "ASC" ? "DSC" : "ASC";
    }
    const sortInputData =
      categorySettingsByGroupName[0].categorySettingsSubGroup;
    const selectedGroupName = selectedCategory;

    SortData(
      sortByCol,
      sortDir,
      "alphanumeric",
      sortInputData,
      (sortedData) => {
        setSortDirection(sortDir);
        setSortKey(sortByCol);
        setCategorySettingsByGroupName([
          {
            groupName: selectedGroupName,
            categorySettingsSubGroup: sortedData,
          },
        ]);
      }
    );
  };

  if (displayError(hasError)) {
    throw new Error(hasError[0]);
  }

  // Fixes a latent bug from the class version: comparing
  // JSON.stringify(selectedCategory) ('"select"') against the raw
  // SELECT constant ("select") was *always* unequal, so this gate did
  // nothing. The render-time `categorySettingsByGroupName.length > 0`
  // check happened to mask that, but the intent was a real comparison.
  const isCategorySelected = selectedCategory !== SELECT;

  if (!isDataLoaded) {
    return null;
  }

  return (
    <>
      <ValidatorForm onSubmit={(e) => handleConfirm(e)}>
        <div className={styles.pagePadding}>
          <Box>
            <Typography variant="h1" sx={{ my: 4 }}>
              Category Settings
            </Typography>
          </Box>
          <Box>
            <Typography variant="body1" sx={{ my: 4 }}>
              {t("CategorySettings.subHeader")}
            </Typography>
          </Box>
          <CategoryDropDown
            {...props}
            styles={styles}
            MenuItem={MenuItem}
            selectedCategory={selectedCategory}
            categoryDropDownValues={categoryDropDownValues}
            updateCategory={updateCategory}
          />
        </div>
        {isCategorySelected && categorySettingsByGroupName.length > 0 && (
          <CategorySettingsRowHeader
            {...props}
            updateMarkupEditEnabled={updateMarkupEditEnabled}
            updateLaborRateEditEnabled={updateLaborRateEditEnabled}
            updateModel={updateModel}
            sortKey={sortKey}
            styles={styles}
            sortColumns={sortColumns}
            sortDirection={sortDirection}
            markupEditEnabled={markupEditEnabled}
            laborRateEditEnabled={laborRateEditEnabled}
            overAllMarkup={overAllMarkup}
            overAllLaborRate={overAllLaborRate}
          />
        )}
        {isCategorySelected && categorySettingsByGroupName.length > 0 && (
          <CategorySettingsTableBody
            styles={styles}
            categorySettingsByGroupName={categorySettingsByGroupName}
            overAllMarkup={overAllMarkup}
            overAllLaborRate={overAllLaborRate}
          />
        )}

        {isCategorySelected && categorySettingsByGroupName.length > 0 && (
          <PublishButton
            {...props}
            styles={styles}
            overridesApplied={overridesApplied}
            enablePublishBtn={enablePublishBtn}
          />
        )}
      </ValidatorForm>
      <ConfirmDialog
        open={confirmDialog || showCategoryChangeDialog}
        showWarningIcon={true}
        dialogProps={confirmDialog ? confirmDialogText : confirmDialogProps}
        onConfirm={confirmDialog ? handleSubmit : changeCategory}
        onCancel={handleClose}
        boldTitle={true}
        onClose={handleClose}
        disableConfirm={
          !Object.hasOwn(
            user?.permissions?.rolePermissions || {},
            "accessoriesSubmit"
          )
        }
        {...props}
      />
    </>
  );
};

const CategorySettingsTableBody = (props) => {
  const { styles, categorySettingsByGroupName, overAllMarkup, overAllLaborRate } =
    props;

  return categorySettingsByGroupName.map((cat) => {
    return (
      cat.categorySettingsSubGroup.length > 0 &&
      cat.categorySettingsSubGroup.map((record, idx) => {
        // namePlate isn't guaranteed unique across catalog ids; fall
        // back to catalogId, then to the index, to avoid duplicate
        // React keys causing render bugs.
        const rowKey =
          (record.catalogId && `${record.namePlate}-${record.catalogId}`) ||
          `${record.namePlate}-${idx}`;

        return (
          <div key={rowKey} className={styles.categoryRow}>
            <Grid container>
              <Grid item xs={4} className={styles.brand}>
                <div className={styles.brandName}>
                  {CapitalizeEachWord(record.namePlate).replace(
                    "Mach-e",
                    "Mach-E"
                  )}
                </div>
              </Grid>
              <Grid item xs={2}>
                <div className={styles.brandValue}>
                  <p className={styles.brandText}>
                    {" "}
                    {overAllMarkup === "" ? record.markup : overAllMarkup}
                    &#37;
                  </p>
                </div>
              </Grid>
              <Grid item xs={2}>
                <div className={styles.brandValue}>
                  <p className={styles.brandText}>
                    {CapitalizeFirstWord(record.markupType)}
                  </p>
                </div>
              </Grid>
              <Grid item xs={2}>
                <div className={styles.brandValue}>
                  <p className={styles.brandText}>
                    &#36;
                    {overAllLaborRate === ""
                      ? record.laborRate
                      : overAllLaborRate}
                  </p>
                </div>
              </Grid>
              <Grid item xs={2}>
                <div className={styles.brandValue}>
                  <p className={styles.brandText}>
                    {CapitalizeFirstWord(record.laborRateType)}
                  </p>
                </div>
              </Grid>
            </Grid>
          </div>
        );
      })
    );
  });
};

CategorySettingsTableBody.propTypes = {
  styles: PropTypes.object.isRequired,
  categorySettingsByGroupName: PropTypes.array.isRequired,
  overAllMarkup: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  overAllLaborRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CategorySettings.propTypes = {
  hideOrShow: PropTypes.func.isRequired,
  user: PropTypes.shape({
    brand: PropTypes.string,
    showWarning: PropTypes.func,
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.object,
    }),
  }).isRequired,
  confirmDialogProps: PropTypes.object,
  t: PropTypes.func.isRequired,
};

export default withTranslation("emp")(CategorySettings);
